/**
 * The people list on the left edge of the by-line views: every name on the
 * week's roster as a draggable chip. The week view drops a name on a line
 * (all week) or a step (one shift); the day view drops on a step. In the
 * day view each chip also says what the person is doing that day.
 */
import React from "react";

import { PersonDay, personDayLabel } from "../utils/day-people";
import { eyebrowSx, lbb } from "../utils/tokens";
import { OUT_NAME_OPACITY } from "./availability-badge";
import { AvailabilityBadge, availabilityStripe, availabilityTitle } from "./availability-badge";
import { useWorkerChipDrag } from "./manual-controls";
import { KnownWorker } from "./standing-crew-panel";

export const WORKER_LIST_WIDTH = 168;

/** A worker's name chip: drag it onto a step or a line. */
export const WorkerChip: React.FC<{
	worker: KnownWorker;
	draftedOn?: string;
	/** The day view's one-line status under the name. */
	status?: PersonDay;
}> = ({ worker, draftedOn, status }) => {
	const { isDragging, dragRef } = useWorkerChipDrag(worker.id, worker.name);
	// The day's marker when one is given, else the week's first confirmed-out row.
	const rows = worker.unavailable ?? [];
	const marker = status ? status.marker : (rows.find((row) => row.kind !== "requested") ?? rows[0] ?? null);
	const outTitle = worker.outLabel ? ` — ${worker.outLabel}` : "";
	const draftTitle = draftedOn ? ` — drafted on ${draftedOn}, applied on Allocate` : "";
	const statusLine = status ? personDayLabel(status) : null;
	const dim = status ? status.state === "out" : Boolean(worker.outLabel);
	return (
		<span
			ref={dragRef}
			role="button"
			tabIndex={0}
			aria-label={`${worker.name}${statusLine ? ` — ${statusLine}` : outTitle}${draftTitle} — drag onto a step to place them`}
			title={`Drag onto a step to place them for that shift${statusLine ? ` — ${statusLine}` : outTitle}${draftTitle}${availabilityTitle(marker)}`}
			style={{
				display: "flex",
				flexDirection: statusLine ? "column" : "row",
				alignItems: statusLine ? "stretch" : "center",
				gap: statusLine ? 1 : 4,
				padding: statusLine ? "4px 10px" : "4px 8px",
				border: draftedOn ? `1px dashed ${lbb.brick}` : `1px solid ${lbb.navy20}`,
				borderRadius: statusLine ? 6 : 999,
				background: isDragging ? lbb.navy : lbb.paper,
				color: isDragging ? lbb.paper : lbb.navy,
				fontFamily: lbb.fontUi,
				fontSize: 12,
				fontWeight: 600,
				cursor: "grab",
				opacity: isDragging ? 0.6 : 1,
				// The list scrolls past its cap; a chip never shrinks to fit.
				flexShrink: 0,
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				userSelect: "none",
				...availabilityStripe(marker),
			}}
		>
			<span style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
				<span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", opacity: dim ? OUT_NAME_OPACITY : 1 }}>{worker.name}</span>
				{draftedOn && (
					<span style={{ fontFamily: lbb.fontMono, fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.brick, flexShrink: 0 }}>
						draft
					</span>
				)}
				{marker && <AvailabilityBadge row={marker} />}
			</span>
			{statusLine && (
				<span
					style={{
						fontFamily: lbb.fontMono,
						fontSize: 10,
						fontWeight: status?.state === "free" ? 700 : 400,
						color: isDragging ? lbb.paper : status?.state === "free" ? lbb.success : status?.state === "out" ? lbb.brick : lbb.ink60,
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{statusLine}
				</span>
			)}
		</span>
	);
};

export const PeopleList: React.FC<{
	title: string;
	hint: string;
	/** Either plain workers (week) or workers with a day status (day). */
	workers?: KnownWorker[];
	people?: PersonDay[];
	draftedOn?: Map<string, string>;
	emptyText: string;
}> = ({ title, hint, workers, people, draftedOn, emptyText }) => {
	const count = people ? people.length : (workers?.length ?? 0);
	const free = people ? people.filter((p) => p.state === "free").length : null;
	return (
		<div style={{ borderRight: lbb.borderHair, padding: "10px 12px 12px", position: "sticky", top: 0, alignSelf: "start" }}>
			<div style={{ ...eyebrowSx, color: lbb.ink60, paddingBottom: 8 }} title={hint}>
				{title} · {count}
				{free !== null && (
					<span style={{ color: free > 0 ? lbb.success : lbb.ink60, marginLeft: 6 }}>· {free} free</span>
				)}
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 640, overflowY: "auto" }}>
				{people
					? people.map((person) => (
							<WorkerChip key={person.worker.id} worker={person.worker} status={person} draftedOn={draftedOn?.get(String(person.worker.id))} />
						))
					: (workers ?? []).map((worker) => <WorkerChip key={worker.id} worker={worker} draftedOn={draftedOn?.get(String(worker.id))} />)}
				{count === 0 && <span style={{ fontFamily: lbb.fontUi, fontSize: 12, color: lbb.navy40 }}>{emptyText}</span>}
			</div>
		</div>
	);
};
